import './viewer.css';
import { escapeHtml } from '../lib/ui';
import { foodEmoji } from '../lib/foodEmoji';
import { mealCountText } from '../lib/mealCount';
import type { Entry, EntryItem, MealType } from '../services/entries';
import {
  fetchSharedOwners,
  fetchOwnerToday,
  fetchOwnerWeights,
  type OwnerToday,
  type WeightPoint,
} from '../services/sharing';

// תצוגת המלווה (מוקאפ מסך 6): קריאה בלבד על היומן ששותף איתי.
// אין ל-viewer גישה לאימייל של הבעלים (RLS) — לכן כותרות גנריות חמות.
// MVP: אם יש כמה שיתופים — מציגים את הראשון (הוותיק ביותר).

const fmt = (n: number): string => Math.round(n).toLocaleString('he-IL');
const round1 = (n: number): number => Math.round(n * 10) / 10;

const MEAL_EMOJI: Record<MealType, string> = { breakfast: '🥣', lunch: '🥗', dinner: '🍽️', snack: '☕' };
const MEAL_NAME: Record<MealType, string> = { breakfast: 'ארוחת בוקר', lunch: 'צהריים', dinner: 'ערב', snack: 'נשנוש' };
const MEAL_ORDER: MealType[] = ['breakfast', 'lunch', 'dinner', 'snack'];
const LRM = '‎'; // כדי שמספרים עם מינוס יוצגו נכון ב-RTL

// ---------- רינדור ----------

export function renderViewer(): string {
  return `
    <div class="viewer">
      <div class="viewer-band">👀 תצוגת ליווי — קריאה בלבד</div>
      <div id="viewer-body">
        <div class="card viewer-loading">
          <div class="viewer-skel"></div>
          <div class="viewer-skel short"></div>
        </div>
      </div>
    </div>`;
}

/** טבעת מיני — כמו במוקאפ מסך 6: r=38, עובי 9 */
function miniRingHtml(eaten: number, target: number | null): string {
  const C = 2 * Math.PI * 38;
  const pct = target && target > 0 ? Math.min(1, eaten / target) : 0;
  const sub = target ? `מתוך ${fmt(target)}` : 'אין יעד עדיין';
  return `
    <div class="vring-wrap">
      <svg viewBox="0 0 92 92" width="92" height="92">
        <circle cx="46" cy="46" r="38" fill="none" stroke="var(--ring-track)" stroke-width="9"/>
        <circle cx="46" cy="46" r="38" fill="none" stroke="var(--ring)" stroke-width="9"
          stroke-linecap="round" stroke-dasharray="${(pct * C).toFixed(1)} ${C.toFixed(1)}"
          transform="rotate(-90 46 46)"/>
      </svg>
      <div class="vring-center">
        <div class="big">${fmt(eaten)}</div>
        <div class="sub">${sub}</div>
      </div>
    </div>`;
}

/** רשימת הסטטוס הקצרה ליד הטבעת — ✓ כשעומדים ביעד */
function statusLinesHtml(today: OwnerToday): string {
  const { entries, totals, targets } = today;
  const lines: string[] = [];

  lines.push(
    entries.length
      ? `<span class="ok">✓</span> תועדו ${mealCountText(entries)}`
      : `<span class="dim">·</span> עוד אין תיעוד היום`,
  );

  // יום בלי תיעוד אינו "עמידה ביעד" — אין נתונים ≠ 0. בלי זה המסך מציג שני ✓
  // ירוקים על יום ריק לגמרי, וזה בדיוק המצב שבו המלווה צריך לדעת שאין מה לראות.
  if (targets && !entries.length) {
    lines.push(`<span class="dim">·</span> היעד היום: ${fmt(targets.calories)} קק"ל` +
      (targets.proteinG > 0 ? ` · ${fmt(targets.proteinG)} ג' חלבון` : ''));
  } else if (targets) {
    const diff = targets.calories - totals.calories;
    lines.push(
      diff >= 0
        ? `<span class="ok">✓</span> בתוך היעד — נשארו ${fmt(diff)} קק"ל`
        : `<span class="warn">⚠</span> חריגה של ${fmt(-diff)} קק"ל`,
    );
    if (targets.proteinG > 0) {
      // "במסלול" = קצב החלבון לא נופל מקצב הקלוריות של היום
      const pacePct = targets.calories > 0 ? totals.calories / targets.calories : 0;
      const onTrack = totals.proteinG >= targets.proteinG * Math.min(1, pacePct);
      lines.push(
        onTrack
          ? `<span class="ok">✓</span> חלבון במסלול (${fmt(totals.proteinG)} ג')`
          : `<span class="dim">·</span> חלבון: ${fmt(totals.proteinG)} מתוך ${fmt(targets.proteinG)} ג'`,
      );
    }
  }
  return lines.map((l) => `<div>${l}</div>`).join('');
}

/** שעה מקומית קצרה של הרישום */
function hhmm(iso: string): string {
  return new Intl.DateTimeFormat('he-IL', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

/** שורת פריט בפירוט — קריאה בלבד: אין נגיעה-לעריכה ואין ✕ */
function itemRowHtml(it: EntryItem, e: Entry): string {
  const qty = it.qty_desc ?? (it.grams ? `${fmt(it.grams)} ג'` : '');
  const sub = [qty, it.protein_g ? `${round1(it.protein_g)} ג׳ חלבון` : '', hhmm(e.eaten_at)]
    .filter(Boolean).map(escapeHtml).join(' · ');
  return `
    <div class="vitem">
      <span class="em">${escapeHtml(it.icon || foodEmoji(it.name_he))}</span>
      <span class="n"><span class="t">${escapeHtml(it.name_he)}</span><span class="s">${sub}</span></span>
      <span class="kc">${fmt(it.calories ?? 0)}</span>
    </div>`;
}

/** פירוט מה נאכל היום, מקובץ לפי ארוחה — מה שהמלווה ביקש לראות מעבר למספר הקלוריות */
function mealsCardHtml(today: OwnerToday): string {
  const { entries } = today;
  if (!entries.length) return '';
  const blocks = MEAL_ORDER.map((type) => {
    const ofType = entries.filter((e) => (e.meal_type ?? 'snack') === type);
    if (!ofType.length) return '';
    const kcal = ofType.reduce((a, e) => a + (e.calories ?? 0), 0);
    const pro = ofType.reduce((a, e) => a + Number(e.protein_g ?? 0), 0);
    // כל רישום הוא מרכיב אחד (מדיניות האפליקציה), אבל נתמכת גם ארוחה מרובת-פריטים ישנה
    const rows = ofType.flatMap((e) => (e.items?.length
      ? e.items.map((it) => itemRowHtml(it, e))
      : [itemRowHtml({ name_he: e.description ?? 'ארוחה', calories: e.calories,
          protein_g: e.protein_g ?? undefined }, e)])).join('');
    return `
      <div class="vmeal">
        <div class="vmeal-head">
          <b>${MEAL_EMOJI[type]} ${MEAL_NAME[type]}</b>
          <span class="vmeal-k">${fmt(kcal)} קק"ל · ${round1(pro)} ג׳</span>
        </div>
        ${rows}
      </div>`;
  }).join('');
  return `
    <div class="card">
      <b class="viewer-card-title">מה היא אכלה היום</b>
      ${blocks}
    </div>`;
}

/** שורת המאקרו של היום — "לא רק קלוריות" */
function macroRowHtml(today: OwnerToday): string {
  const { totals, targets } = today;
  const cell = (cls: string, label: string, val: number, target: number | undefined) => `
    <div class="vmacro ${cls}">
      <span class="lbl">${label}</span>
      <span class="val">${fmt(val)}${target ? ` <i>/ ${fmt(target)}</i>` : ''}</span>
    </div>`;
  return `<div class="vmacros">
    ${cell('p', 'חלבון', totals.proteinG, targets?.proteinG)}
    ${cell('c', 'פחמימות', totals.carbsG, targets?.carbsG)}
    ${cell('f', 'שומן', totals.fatG, targets?.fatG)}
  </div>`;
}

function todayCardHtml(today: OwnerToday): string {
  const last = today.entries[today.entries.length - 1];
  const updated = last
    ? `עדכון אחרון ${new Intl.DateTimeFormat('he-IL', { hour: '2-digit', minute: '2-digit' }).format(new Date(last.eaten_at))}`
    : new Intl.DateTimeFormat('he-IL', { weekday: 'long' }).format(new Date());
  return `
    <div class="card">
      <div class="h-row">
        <b class="viewer-card-title">היום שלה 🌸</b>
        <span class="muted">${updated}</span>
      </div>
      <div class="viewer-today">
        ${miniRingHtml(today.totals.calories, today.targets?.calories ?? null)}
        <div class="vstatus">${statusLinesHtml(today)}</div>
      </div>
      ${today.entries.length ? macroRowHtml(today) : ''}
    </div>`;
}

/** sparkline פשוט — מנורמל ל-viewBox קבוע, נקודה אחרונה מודגשת */
function sparklineHtml(points: WeightPoint[]): string {
  const W = 320;
  const H = 64;
  const PAD_X = 10;
  const PAD_Y = 8;
  const min = Math.min(...points.map((p) => p.weightKg));
  const max = Math.max(...points.map((p) => p.weightKg));
  const span = max - min;
  const x = (i: number): number =>
    points.length === 1 ? W / 2 : PAD_X + (i / (points.length - 1)) * (W - 2 * PAD_X);
  const y = (w: number): number =>
    span === 0 ? H / 2 : PAD_Y + ((max - w) / span) * (H - 2 * PAD_Y);
  const path = points.map((p, i) => `${x(i).toFixed(1)},${y(p.weightKg).toFixed(1)}`).join(' ');
  const lastPt = points[points.length - 1]!;
  return `
    <svg viewBox="0 0 ${W} ${H}" width="100%" class="viewer-spark" aria-hidden="true">
      <polyline points="${path}" fill="none" stroke="var(--fat)" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="${x(points.length - 1).toFixed(1)}" cy="${y(lastPt.weightKg).toFixed(1)}" r="5"
        fill="var(--fat)" stroke="#fff" stroke-width="2"/>
    </svg>`;
}

function weightCardHtml(weights: WeightPoint[]): string {
  if (!weights.length) {
    return `
      <div class="card">
        <b class="viewer-card-title">מגמת משקל</b>
        <p class="muted viewer-noweights">עוד אין שקילות — המגמה תופיע כאן 🌱</p>
      </div>`;
  }
  const first = weights[0]!;
  const last = weights[weights.length - 1]!;
  const delta = last.weightKg - first.weightKg;
  const spanDays = Math.round(
    (new Date(last.date).getTime() - new Date(first.date).getTime()) / 86400000,
  );
  const weeks = Math.max(1, Math.round(spanDays / 7));
  const deltaHtml =
    weights.length >= 2 && spanDays >= 1
      ? `<span class="vdelta ${delta > 0 ? 'up' : ''}">${LRM}${delta >= 0 ? '+' : ''}${delta.toFixed(1)} ק"ג ב-${weeks === 1 ? 'שבוע' : `${weeks} שבועות`} ${delta > 0 ? '↑' : '↓'}</span>`
      : '';
  return `
    <div class="card">
      <b class="viewer-card-title">מגמת משקל</b>
      <div class="viewer-weight-row">
        <div class="vhero"><span class="num">${LRM}${last.weightKg.toFixed(1)}</span><span class="unit">ק"ג</span></div>
        ${deltaHtml}
      </div>
      ${weights.length >= 2 ? sparklineHtml(weights) : `<div class="muted viewer-noweights">צריך עוד כמה שקילות כדי לצייר מגמה</div>`}
    </div>`;
}

function emptyHtml(): string {
  return `
    <div class="card viewer-empty">
      <div class="big">🤝</div>
      <b>אף אחת עוד לא שיתפה איתך יומן</b>
      <p class="muted">כשבת הזוג תפעיל שיתוף מהאפליקציה שלה — היומן שלה יופיע כאן, בקריאה בלבד.</p>
    </div>`;
}

// ---------- טעינה וחיבור ----------

async function refresh(root: HTMLElement): Promise<void> {
  const body = root.querySelector<HTMLElement>('#viewer-body');
  if (!body) return;
  try {
    const owners = await fetchSharedOwners();
    if (!root.isConnected) return;
    if (!owners.length) {
      body.innerHTML = emptyHtml();
      return;
    }
    const ownerId = owners[0]!;
    const [today, weights] = await Promise.all([
      fetchOwnerToday(ownerId),
      fetchOwnerWeights(ownerId),
    ]);
    if (!root.isConnected) return;
    body.innerHTML = todayCardHtml(today) + mealsCardHtml(today) + weightCardHtml(weights);
  } catch {
    if (!root.isConnected) return;
    body.innerHTML = `
      <div class="card viewer-empty">
        <p class="muted">לא הצלחנו לטעון את היומן המשותף — בדוק חיבור.</p>
        <button class="btn-primary" id="viewer-retry">ניסיון חוזר</button>
      </div>`;
    body.querySelector('#viewer-retry')?.addEventListener('click', () => void refresh(root));
  }
}

export function attachViewer(root: HTMLElement): void {
  void refresh(root);
}
